import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { renderSlot as _renderSlot, createVNode as _createVNode, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, withKeys as _withKeys, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createBlock as _createBlock } from "vue";
const _hoisted_1 = ["aria-label"];
import { computed, nextTick, ref } from "vue";
import { getTabbableElements } from "@/common/helpers/keyboard-navigation";
// Components
import RecCard from "@/common/components/RecCard.vue";
import SvgIcon from "@/common/components/SvgIcon.vue";
import Dialog from "primevue/dialog";
// Setup
export default /*@__PURE__*/ _defineComponent({
    __name: 'RecDialog',
    props: /*@__PURE__*/ _mergeModels({
        hideUnderlay: { type: Boolean },
        position: { default: "center" },
        size: { default: "small" },
        closable: { type: Boolean, default: true },
        closeOnClickOutside: { type: Boolean },
        closeOnEscape: { type: Boolean, default: true },
        style: { default: "" },
        dialogPT: { default: () => ({}) },
        padding: { default: 30 }
    }, {
        "modelValue": { type: Boolean, ...{ default: true } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["after-hide", "click-outside", "close"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        /********************************************
         * RecDialog
         *
         * A dialog component that wraps PrimeVue's Dialog component.
         *
         * Props:
         * - hideUnderlay: Whether the dialog should hide the underlay. Defaults to false.
         * - position: The position of the dialog. Can be "center", "left", "top", "bottom", "right", "topleft", "topright", "bottomleft", or "bottomright". Defaults to "center".
         * - size: The size of the dialog. Can be "small", "medium", "large" or "x-large". Defaults to "small".
         * - closable: Whether the dialog can be closed. Defaults to true.
         * - closeOnClickOutside: Whether the dialog should close when clicking outside of it. Defaults to false.
         * - closeOnEscape: Whether the dialog should close when the Escape key is pressed. Defaults to true.
         * - style: Custom styles for the dialog.
         * - dialogPT: Pass-through attributes for the PrimeVue Dialog component.
         * - padding: Padding inside the dialog card. Defaults to 30.
         *
         * Emits:
         * - click-outside: Emitted when the user clicks the mask area outside the dialog.
         *
         * @slot: Default slot for custom content inside the dialog.
         *
         * @example
         * <RecDialog v-model="showDialog" size="small" close-on-click-outside @hide="handleCancel">
         *  <RecCardTitle color="danger">
         *   {{ $t("Discard Draft Reply") }}
         *  </RecCardTitle>
         *  <RecCardContent>
         *   <div class="rc-mb20">{{ $t("Are you sure you want to discard your draft reply?") }}</div>
         *  </RecCardContent>
         *  <RecCardFooter>
         *   <button class="btn btn-danger" @click="handleConfirm">{{ $t("Discard") }}</button>
         *   <button class="btn" @click="handleCancel">{{ $t("Cancel") }}</button>
         *  </RecCardFooter>
         * </RecDialog>
         *
         * @see https://primevue.org/dialog/
         *
         *********************************************/
        const props = __props;
        const emit = __emit;
        const model = _useModel(__props, "modelValue");
        const dialogClasses = computed(() => {
            return {
                "rec-dialog": true,
                [`rec-dialog--${props.size}`]: true
            };
        });
        const closeModal = () => {
            model.value = false;
            emit("close");
        };
        const onEscape = () => {
            if (props.closeOnEscape && props.closable) {
                closeModal();
            }
        };
        const dialogContent = ref(null);
        const focusFirstTabbableElement = () => {
            nextTick(() => {
                if (dialogContent.value) {
                    const tabbableElements = getTabbableElements(dialogContent.value);
                    if (tabbableElements.length > 0) {
                        tabbableElements[0].focus();
                    }
                }
            });
        };
        const handleClickOutside = (event) => {
            if (event.target === event.currentTarget) {
                emit("click-outside");
            }
        };
        const computedDialogPT = computed(() => {
            const pt = props.dialogPT ?? {};
            const mask = pt.mask ?? {};
            return {
                ...pt,
                mask: {
                    ...mask,
                    onClick: (e) => {
                        handleClickOutside(e);
                        if (typeof mask.onClick === "function")
                            mask.onClick(e);
                    }
                }
            };
        });
        const handleAfterHide = () => {
            emit("after-hide");
            // Keep body overflow hidden if any PrimeVue dialogs remain open.
            setTimeout(() => {
                const openDialogs = document.querySelectorAll('.p-dialog.p-component[aria-modal="true"]');
                if (openDialogs.length > 0) {
                    document.body.classList.add("p-overflow-hidden");
                    document.body.style.setProperty("--scrollbar-width", "0px");
                }
                else {
                    document.body.classList.remove("p-overflow-hidden");
                    document.body.style.setProperty("--scrollbar-width", "");
                }
            }, 10);
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Dialog), {
                visible: model.value,
                "onUpdate:visible": _cache[0] || (_cache[0] = ($event) => ((model).value = $event)),
                modal: !__props.hideUnderlay,
                class: _normalizeClass(dialogClasses.value),
                "dismissable-mask": __props.closeOnClickOutside,
                "close-on-escape": false,
                position: __props.position,
                style: _normalizeStyle(__props.style),
                pt: computedDialogPT.value,
                onShow: focusFirstTabbableElement,
                onAfterHide: handleAfterHide
            }, {
                container: _withCtx(() => [
                    _createElementVNode("div", {
                        ref_key: "dialogContent",
                        ref: dialogContent,
                        class: "rec-dialog__content",
                        onKeydown: _withKeys(onEscape, ["esc"])
                    }, [
                        _createVNode(RecCard, { padding: __props.padding }, {
                            default: _withCtx(() => [
                                _renderSlot(_ctx.$slots, "default"),
                                (__props.closable)
                                    ? (_openBlock(), _createElementBlock("button", {
                                        key: 0,
                                        class: "rec-dialog__close",
                                        "aria-label": _ctx.$t('Close'),
                                        onClick: _withModifiers(closeModal, ["stop"])
                                    }, [
                                        _renderSlot(_ctx.$slots, "close-button-icon", {}, () => [
                                            _createVNode(SvgIcon, { icon: "icon-close-3" })
                                        ])
                                    ], 8, _hoisted_1))
                                    : _createCommentVNode("", true)
                            ]),
                            _: 3
                        }, 8, ["padding"])
                    ], 544)
                ]),
                _: 3
            }, 8, ["visible", "modal", "class", "dismissable-mask", "position", "style", "pt"]));
        };
    }
});
